import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import client from '../../../lib/feathers';

/**
 * Change history for a listing — who changed what, when. Reads the change-audit
 * log (admin sees all; industry is scoped server-side to a listing they manage).
 * Every listing/wine/vintage/hours edit lands here, attributed to the real
 * actor — the accountability layer that lets an owner trust their data.
 */

function displayName(u) {
  if (!u) return 'Someone';
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (u.username) return `@${u.username}`;
  if (u.email) return u.email;
  return 'Someone';
}

function whoDidIt(row) {
  const actor = displayName(row.actor);
  // Set only for impersonation sessions (feature lands next); shows the real
  // admin behind an "acting as" change.
  return row.impersonator ? `${displayName(row.impersonator)} (as ${actor})` : actor;
}

// Heading for a field in the expanded diff. Mirrors the server's summary
// labels, but as a standalone title (no leading "the").
const FIELD_TITLE = {
  hours: 'Hours',
  address: 'Address',
  location: 'Map location',
  contact: 'Contact info',
  categories: 'Categories',
  wineTypes: 'Wine types',
  wineVarietals: 'Varietals',
  name: 'Name',
  shortDescription: 'Description',
  description: 'Description',
  tastingFeeRange: 'Tasting fee range',
  aliases: 'Also-known-as names',
  status: 'Status',
  isExcluded: 'Hidden from the site',
  listingName: 'Producer name',
  brand: 'Brand',
  ava: 'Appellation',
};

// camelCase -> "Camel case", so a field we haven't labelled still reads as
// words rather than as a column name.
function fieldTitle(f) {
  if (FIELD_TITLE[f]) return FIELD_TITLE[f];
  const spaced = f.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const PAGE = 40;

export default function ChangeHistoryPanel() {
  const listing = useSelector((s) => s.listings.selected);
  const loggedInUser = useSelector((s) => s.auth.user);
  const role = loggedInUser?.role;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  // Default view = changes the owner/manager made. Admin + enrichment edits are
  // hidden until toggled on.
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async (skip = 0) => {
    if (!listing?.id || !role) return;
    setLoading(true);
    setError(null);
    try {
      const query = { listingId: listing.id, $limit: PAGE, $skip: skip };
      if (!showAll) query.actorRole = 'industry';
      const res = await client.service(`${role}/change-audit`).find({ query });
      setTotal(res.total || 0);
      setRows((prev) => (skip === 0 ? res.data : [...prev, ...res.data]));
    } catch (err) {
      setError(err?.message || 'Could not load change history');
    } finally {
      setLoading(false);
    }
  }, [listing?.id, role, showAll]);

  useEffect(() => { load(0); }, [load]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Change history</h3>
        <p className="mt-1 text-sm text-gray-500">
          Changes made to this listing, its wines, and hours — with who made them.
        </p>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="rounded border-gray-300 text-red-800 focus:ring-red-700"
          />
          Show admin &amp; automated changes
        </label>
      </div>

      {error && (
        <div className="px-4 py-4 text-sm text-red-600">{error}</div>
      )}

      {!error && rows.length === 0 && !loading && (
        <div className="px-4 py-8 text-sm text-gray-500 text-center">
          {showAll
            ? 'No changes recorded yet.'
            : 'No owner changes yet. Toggle above to see admin & automated changes.'}
        </div>
      )}

      <ul className="divide-y divide-gray-200">
        {rows.map((row) => {
          const fields = row.changes && typeof row.changes === 'object' ? Object.keys(row.changes) : [];
          const isOpen = !!expanded[row.id];
          return (
            <li key={row.id} className="px-4 py-3 sm:px-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{whoDidIt(row)}</span>
                    {' '}
                    {row.summary}
                    {row.actorRole && row.actorRole !== 'industry' && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 uppercase tracking-wide align-middle">
                        {row.actorRole}
                      </span>
                    )}
                  </p>
                  {fields.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggle(row.id)}
                      className="mt-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                      {isOpen ? 'Hide details' : `Show what changed (${fields.length})`}
                    </button>
                  )}
                  {isOpen && (
                    <dl className="mt-2 rounded-md bg-gray-50 p-3 text-xs text-gray-600 space-y-3">
                      {fields.map((f) => {
                        const change = row.changes[f];
                        const isDiff = change && typeof change === 'object' && ('from' in change || 'to' in change);
                        return (
                          <div key={f}>
                            <dt className="font-medium text-gray-700">{fieldTitle(f)}</dt>
                            {isDiff ? (
                              <dd className="mt-0.5">
                                <div className="text-[10px] uppercase tracking-wide text-gray-400">Before</div>
                                <Value field={f} value={change.from} muted />
                                <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">After</div>
                                <Value field={f} value={change.to} />
                              </dd>
                            ) : (
                              <dd className="mt-0.5"><Value field={f} value={change} /></dd>
                            )}
                          </div>
                        );
                      })}
                    </dl>
                  )}
                </div>
                <time className="flex-none text-xs text-gray-400 whitespace-nowrap" title={new Date(row.createdAt).toLocaleString()}>
                  {timeAgo(row.createdAt)}
                </time>
              </div>
            </li>
          );
        })}
      </ul>

      {rows.length < total && (
        <div className="px-4 py-3 sm:px-6 border-t border-gray-200 text-center">
          <button
            type="button"
            onClick={() => load(rows.length)}
            disabled={loading}
            className="text-sm text-red-800 hover:text-red-900 disabled:opacity-50"
          >
            {loading ? 'Loading…' : `Load more (${total - rows.length} older)`}
          </button>
        </div>
      )}
    </div>
  );
}

// A GeoJSON Point stores [lng, lat]; humans read lat, lng.
function formatPoint(v) {
  const c = v && v.coordinates;
  if (!Array.isArray(c) || c.length < 2) return null;
  const round = (n) => (typeof n === 'number' ? n.toFixed(5) : n);
  return `${round(c[1])}, ${round(c[0])}`;
}

function formatAddress(v) {
  const line = [v.address1, v.address2].filter(Boolean).join(' ');
  const city = [v.city, v.state].filter(Boolean).join(', ');
  return [line, [city, v.zip].filter(Boolean).join(' '), v.country].filter(Boolean).join(' · ');
}

// Returns a display string, or null when the value needs a JSON block (hours,
// contact — nested shapes a one-liner would mangle).
function formatValue(field, v) {
  if (v === null || v === undefined || v === '') return 'not set';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    if (!v.length) return 'none';
    if (v.every((x) => typeof x !== 'object')) return v.join(', ');
    return null;
  }
  if (typeof v === 'object') {
    if (field === 'location') return formatPoint(v) || null;
    if (field === 'address') return formatAddress(v) || null;
    return null;
  }
  return String(v);
}

function Value({ field, value, muted }) {
  const text = formatValue(field, value);
  if (text !== null) {
    return <span className={muted ? 'line-through text-gray-400 break-all' : 'text-gray-800 break-all'}>{text}</span>;
  }
  let json;
  try { json = JSON.stringify(value, null, 2); } catch (e) { json = String(value); }
  return (
    <pre className={`mt-0.5 max-h-40 overflow-auto rounded bg-white p-2 text-[11px] leading-snug whitespace-pre-wrap break-all ${muted ? 'text-gray-400' : 'text-gray-700'}`}>
      {json}
    </pre>
  );
}
